/**
 * Tabellen-Dateien selbst geschrieben und gelesen: CSV (deutsches Excel: Semikolon, UTF-8 mit BOM)
 * und Excel (.xlsx = Zip mit XML). Ohne fremde Bibliotheken; Packen über CompressionStream.
 * Läuft im Browser und in Node (für Tests).
 */

export type Zelle = string | number | boolean | null | { datum: string };

export interface Blatt {
  name: string;
  kopf: string[];
  zeilen: Zelle[][];
  /** Gliederungsebene je Zeile (Excel kann Zeilen gruppieren). */
  ebenen?: number[];
  /** Spaltenarten für Excel-Formate. */
  arten?: ('text' | 'zahl' | 'betrag' | 'datum')[];
  /** Spalte, deren Text je Ebene eingerückt wird. */
  einrueckSpalte?: number;
}

// ------------------------------------------------------------------ CSV

function csvFeld(z: Zelle): string {
  let t: string;
  if (z === null || z === undefined) t = '';
  else if (typeof z === 'object') t = datumDeutsch(z.datum);
  else if (typeof z === 'boolean') t = z ? 'ja' : '';
  else if (typeof z === 'number') t = String(z).replace('.', ',');
  else t = z;
  return /[;"\r\n]/.test(t) || /^\s|\s$/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
}

export function csvSchreiben(b: Blatt): string {
  const zeilen = [b.kopf, ...b.zeilen].map((z) => z.map(csvFeld).join(';'));
  return `﻿${zeilen.join('\r\n')}\r\n`;
}

/** Liest CSV; erkennt Trennzeichen (; , Tab) an der ersten Zeile. */
export function csvLesen(text: string): string[][] {
  const t = text.replace(/^﻿/, '');
  const ersteZeile = t.slice(0, t.search(/\r?\n|$/));
  const kandidaten = [';', ',', '\t'];
  const trenner = kandidaten.reduce((a, b) => (ersteZeile.split(b).length > ersteZeile.split(a).length ? b : a), ';');
  const aus: string[][] = [];
  let zeile: string[] = [];
  let feld = '';
  let inAnf = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!;
    if (inAnf) {
      if (c === '"') {
        if (t[i + 1] === '"') {
          feld += '"';
          i++;
        } else inAnf = false;
      } else feld += c;
      continue;
    }
    if (c === '"' && feld === '') inAnf = true;
    else if (c === trenner) {
      zeile.push(feld);
      feld = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && t[i + 1] === '\n') i++;
      zeile.push(feld);
      aus.push(zeile);
      zeile = [];
      feld = '';
    } else feld += c;
  }
  if (feld !== '' || zeile.length) {
    zeile.push(feld);
    aus.push(zeile);
  }
  return aus.filter((z) => z.some((f) => f.trim() !== ''));
}

// ------------------------------------------------------------------ Zip

const CRC_TAFEL = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(d: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < d.length; i++) c = CRC_TAFEL[(c ^ d[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

async function durch(daten: Uint8Array, strom: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const s = new Blob([daten as BlobPart]).stream().pipeThrough(strom as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
  return new Uint8Array(await new Response(s).arrayBuffer());
}

const kodierer = new TextEncoder();
const dekodierer = new TextDecoder();

export async function zipPacken(dateien: { name: string; inhalt: string | Uint8Array }[]): Promise<Uint8Array> {
  const teile: Uint8Array[] = [];
  const verzeichnis: Uint8Array[] = [];
  let versatz = 0;
  for (const d of dateien) {
    const roh = typeof d.inhalt === 'string' ? kodierer.encode(d.inhalt) : d.inhalt;
    const gepackt = await durch(roh, new CompressionStream('deflate-raw'));
    const name = kodierer.encode(d.name);
    const crc = crc32(roh);
    const kopf = new DataView(new ArrayBuffer(30));
    kopf.setUint32(0, 0x04034b50, true);
    kopf.setUint16(4, 20, true);
    kopf.setUint16(6, 0x0800, true); // UTF-8-Namen
    kopf.setUint16(8, 8, true); // deflate
    kopf.setUint16(10, 0, true);
    kopf.setUint16(12, 0x21, true);
    kopf.setUint32(14, crc, true);
    kopf.setUint32(18, gepackt.length, true);
    kopf.setUint32(22, roh.length, true);
    kopf.setUint16(26, name.length, true);
    kopf.setUint16(28, 0, true);
    const z = new DataView(new ArrayBuffer(46));
    z.setUint32(0, 0x02014b50, true);
    z.setUint16(4, 20, true);
    z.setUint16(6, 20, true);
    z.setUint16(8, 0x0800, true);
    z.setUint16(10, 8, true);
    z.setUint16(12, 0, true);
    z.setUint16(14, 0x21, true);
    z.setUint32(16, crc, true);
    z.setUint32(20, gepackt.length, true);
    z.setUint32(24, roh.length, true);
    z.setUint16(28, name.length, true);
    z.setUint32(42, versatz, true);
    teile.push(new Uint8Array(kopf.buffer), name, gepackt);
    verzeichnis.push(new Uint8Array(z.buffer), name);
    versatz += 30 + name.length + gepackt.length;
  }
  const vGroesse = verzeichnis.reduce((s, x) => s + x.length, 0);
  const ende = new DataView(new ArrayBuffer(22));
  ende.setUint32(0, 0x06054b50, true);
  ende.setUint16(8, dateien.length, true);
  ende.setUint16(10, dateien.length, true);
  ende.setUint32(12, vGroesse, true);
  ende.setUint32(16, versatz, true);
  const alle = [...teile, ...verzeichnis, new Uint8Array(ende.buffer)];
  const aus = new Uint8Array(alle.reduce((s, x) => s + x.length, 0));
  let p = 0;
  for (const x of alle) {
    aus.set(x, p);
    p += x.length;
  }
  return aus;
}

export async function zipEntpacken(d: Uint8Array): Promise<Map<string, Uint8Array>> {
  const v = new DataView(d.buffer, d.byteOffset, d.byteLength);
  let e = d.length - 22;
  while (e >= 0 && v.getUint32(e, true) !== 0x06054b50) e--;
  if (e < 0) throw new Error('Das ist keine gültige Excel-Datei (kein Zip).');
  const anzahl = v.getUint16(e + 10, true);
  let p = v.getUint32(e + 16, true);
  const aus = new Map<string, Uint8Array>();
  for (let i = 0; i < anzahl; i++) {
    if (v.getUint32(p, true) !== 0x02014b50) break;
    const art = v.getUint16(p + 10, true);
    const gepacktLaenge = v.getUint32(p + 20, true);
    const nameLaenge = v.getUint16(p + 28, true);
    const extraLaenge = v.getUint16(p + 30, true);
    const kommentarLaenge = v.getUint16(p + 32, true);
    const lokal = v.getUint32(p + 42, true);
    const name = dekodierer.decode(d.subarray(p + 46, p + 46 + nameLaenge));
    const lNameLaenge = v.getUint16(lokal + 26, true);
    const lExtraLaenge = v.getUint16(lokal + 28, true);
    const start = lokal + 30 + lNameLaenge + lExtraLaenge;
    const roh = d.subarray(start, start + gepacktLaenge);
    if (art === 0) aus.set(name, roh);
    else if (art === 8) aus.set(name, await durch(roh, new DecompressionStream('deflate-raw')));
    p += 46 + nameLaenge + extraLaenge + kommentarLaenge;
  }
  return aus;
}

// ------------------------------------------------------------------ Excel schreiben

const xml = (t: string) =>
  t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');

function spaltenName(i: number): string {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

function spaltenIndex(ref: string): number {
  let n = 0;
  for (const c of ref.replace(/\d+$/, '')) n = n * 26 + (c.charCodeAt(0) - 64);
  return n - 1;
}

/** ISO-Datum → Excel-Seriennummer. */
export function excelDatum(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!) / 86400000 + 25569;
}

export function datumDeutsch(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

// Stile: 0 normal, 1 Kopf, 2 Datum, 3 Betrag, 4 Zahl, 5+n Einrückung n (0–9)
const STILE = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0.00"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFE3EBFF"/><bgColor indexed="64"/></patternFill></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="15">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"><alignment vertical="top" wrapText="1"/></xf>
<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
<xf numFmtId="14" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"><alignment vertical="top"/></xf>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"><alignment vertical="top"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"><alignment vertical="top"/></xf>
${Array.from({ length: 10 }, (_, n) => `<xf numFmtId="0" fontId="${n === 0 ? 1 : 0}" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment indent="${n}" vertical="top" wrapText="1"/></xf>`).join('\n')}
</cellXfs>
</styleSheet>`;

export async function xlsxSchreiben(b: Blatt): Promise<Uint8Array> {
  const zeilenXml: string[] = [];
  const zelle = (r: number, c: number, w: Zelle, art: string | undefined, ebene: number): string => {
    const ref = `${spaltenName(c)}${r}`;
    if (w === null || w === undefined || w === '') return '';
    if (typeof w === 'object') {
      const n = excelDatum(w.datum);
      return n === null ? '' : `<c r="${ref}" s="2"><v>${n}</v></c>`;
    }
    if (typeof w === 'boolean') return `<c r="${ref}" t="b"><v>${w ? 1 : 0}</v></c>`;
    if (typeof w === 'number') return `<c r="${ref}" s="${art === 'betrag' ? 3 : 4}"><v>${w}</v></c>`;
    const s = c === b.einrueckSpalte ? 5 + Math.min(9, ebene) : 0;
    return `<c r="${ref}" t="inlineStr" s="${s}"><is><t xml:space="preserve">${xml(w)}</t></is></c>`;
  };
  zeilenXml.push(`<row r="1">${b.kopf.map((k, c) => `<c r="${spaltenName(c)}1" t="inlineStr" s="1"><is><t>${xml(k)}</t></is></c>`).join('')}</row>`);
  b.zeilen.forEach((z, i) => {
    const r = i + 2;
    const ebene = b.ebenen?.[i] ?? 0;
    const gliederung = ebene > 0 ? ` outlineLevel="${Math.min(7, ebene)}"` : '';
    zeilenXml.push(`<row r="${r}"${gliederung}>${z.map((w, c) => zelle(r, c, w, b.arten?.[c], ebene)).join('')}</row>`);
  });
  const breiten = b.kopf.map((k, c) => {
    const laengste = Math.max(k.length, ...b.zeilen.slice(0, 500).map((z) => String(typeof z[c] === 'object' && z[c] ? '00.00.0000' : (z[c] ?? '')).length + (c === b.einrueckSpalte ? 4 : 0)));
    return Math.min(60, Math.max(8, laengste + 2));
  });
  const blatt = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetPr><outlinePr summaryBelow="0"/></sheetPr>
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
<cols>${breiten.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>
<sheetData>${zeilenXml.join('')}</sheetData>
<autoFilter ref="A1:${spaltenName(Math.max(0, b.kopf.length - 1))}${b.zeilen.length + 1}"/>
</worksheet>`;
  const name = xml(b.name.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Tabelle');
  return zipPacken([
    {
      name: '[Content_Types].xml',
      inhalt: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`,
    },
    {
      name: '_rels/.rels',
      inhalt: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      inhalt: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${name}" sheetId="1" r:id="rId1"/></sheets><definedNames><definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">'${name.replace(/'/g, "''")}'!$A$1:$${spaltenName(Math.max(0, b.kopf.length - 1))}$${b.zeilen.length + 1}</definedName></definedNames></workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      inhalt: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    { name: 'xl/styles.xml', inhalt: STILE },
    { name: 'xl/worksheets/sheet1.xml', inhalt: blatt },
  ]);
}

// ------------------------------------------------------------------ Excel lesen

const entities = (t: string) =>
  t
    .replace(/&#x([0-9a-f]+);/gi, (_, h: string) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

/** Alle <t>-Texte eines Stücks XML (Rich-Text besteht aus mehreren <r><t>). */
function texte(stueck: string): string {
  let s = '';
  for (const m of stueck.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>|<t(?:\s[^>]*)?\/>/g)) s += m[1] ?? '';
  return entities(s);
}

export interface GeleseneTabelle {
  zeilen: (string | number | boolean | null)[][];
  /** Gliederungsebene je Zeile aus Excel (outlineLevel), falls vorhanden. */
  ebenen: number[];
  /** Spalten, deren Zahlen als Datum formatiert sind. */
  datumSpalten: Set<number>;
  /** Einrückung je Zeile und Spalte (Excel-Ausrichtung). */
  einrueckung: number[][];
}

export async function xlsxLesen(d: Uint8Array): Promise<GeleseneTabelle> {
  const dateien = await zipEntpacken(d);
  const lies = (n: string) => {
    const x = dateien.get(n);
    return x ? dekodierer.decode(x) : '';
  };
  // Erstes Blatt über die Arbeitsmappe finden
  const mappe = lies('xl/workbook.xml');
  const rels = lies('xl/_rels/workbook.xml.rels');
  const ersteId = /<sheet\b[^>]*\br:id="([^"]+)"/.exec(mappe)?.[1];
  let ziel = ersteId ? new RegExp(`<Relationship\\b[^>]*Id="${ersteId}"[^>]*Target="([^"]+)"`).exec(rels)?.[1] : undefined;
  if (!ziel && ersteId) ziel = new RegExp(`<Relationship\\b[^>]*Target="([^"]+)"[^>]*Id="${ersteId}"`).exec(rels)?.[1];
  const pfad = ziel ? (ziel.startsWith('/') ? ziel.slice(1) : `xl/${ziel}`).replace(/\/\.\//g, '/') : 'xl/worksheets/sheet1.xml';
  const blatt = lies(pfad) || lies('xl/worksheets/sheet1.xml');
  if (!blatt) throw new Error('In der Excel-Datei wurde kein Tabellenblatt gefunden.');

  const geteilt: string[] = [];
  for (const m of lies('xl/sharedStrings.xml').matchAll(/<si>([\s\S]*?)<\/si>/g)) geteilt.push(texte(m[1]!));

  // Stile: welche Formate sind Datumsformate, welche Einrückung haben sie?
  const stile = lies('xl/styles.xml');
  const eigeneFormate = new Map<number, string>();
  for (const m of stile.matchAll(/<numFmt\b[^>]*numFmtId="(\d+)"[^>]*formatCode="([^"]*)"/g)) eigeneFormate.set(Number(m[1]), entities(m[2]!));
  const xfs = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(stile)?.[1] ?? '';
  const stilDatum: boolean[] = [];
  const stilEinzug: number[] = [];
  for (const m of xfs.matchAll(/<xf\b([^>]*?)(?:\/>|>([\s\S]*?)<\/xf>)/g)) {
    const id = Number(/numFmtId="(\d+)"/.exec(m[1]!)?.[1] ?? 0);
    const code = eigeneFormate.get(id) ?? '';
    stilDatum.push((id >= 14 && id <= 22) || (id >= 45 && id <= 47) || (/[dy]/i.test(code.replace(/"[^"]*"|\[[^\]]*\]/g, '')) && !/^[#0.,\s]+$/.test(code)));
    stilEinzug.push(Number(/indent="(\d+)"/.exec(m[2] ?? '')?.[1] ?? 0));
  }

  const zeilen: GeleseneTabelle['zeilen'] = [];
  const ebenen: number[] = [];
  const einrueckung: number[][] = [];
  const datumSpalten = new Set<number>();
  for (const zm of blatt.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const attr = zm[1]!;
    const r = Number(/\br="(\d+)"/.exec(attr)?.[1] ?? zeilen.length + 1) - 1;
    const z: (string | number | boolean | null)[] = [];
    const e: number[] = [];
    let nextC = 0;
    for (const cm of (zm[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const ca = cm[1]!;
      const ref = /\br="([A-Z]+\d*)"/.exec(ca)?.[1];
      const c = ref ? spaltenIndex(ref) : nextC;
      nextC = c + 1;
      const t = /\bt="([^"]+)"/.exec(ca)?.[1] ?? 'n';
      const s = Number(/\bs="(\d+)"/.exec(ca)?.[1] ?? 0);
      const innen = cm[2] ?? '';
      const v = /<v>([\s\S]*?)<\/v>/.exec(innen)?.[1];
      let w: string | number | boolean | null = null;
      if (t === 's') w = v !== undefined ? (geteilt[Number(v)] ?? '') : null;
      else if (t === 'inlineStr') w = texte(/<is>([\s\S]*?)<\/is>/.exec(innen)?.[1] ?? '');
      else if (t === 'str' || t === 'e') w = v !== undefined ? entities(v) : null;
      else if (t === 'b') w = v === '1';
      else if (v !== undefined) {
        w = Number(v);
        if (stilDatum[s] && r > 0) datumSpalten.add(c);
      }
      z[c] = w;
      e[c] = stilEinzug[s] ?? 0;
    }
    for (let i = 0; i < z.length; i++) if (z[i] === undefined) z[i] = null;
    while (zeilen.length < r) {
      zeilen.push([]);
      ebenen.push(0);
      einrueckung.push([]);
    }
    zeilen.push(z);
    ebenen.push(Number(/outlineLevel="(\d+)"/.exec(attr)?.[1] ?? 0));
    einrueckung.push(e);
  }
  // Leere Zeilen entfernen (Ebenen parallel halten)
  const behalten = zeilen.map((z) => z.some((w) => w !== null && String(w).trim() !== ''));
  return {
    zeilen: zeilen.filter((_, i) => behalten[i]),
    ebenen: ebenen.filter((_, i) => behalten[i]),
    einrueckung: einrueckung.filter((_, i) => behalten[i]),
    datumSpalten,
  };
}
